import { get } from 'lodash'
import { useEffect, useState } from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import styled from 'styled-components'

import { Audience, AudienceFilter as Filter } from '@/utilities/AudienceTarget/interfaces'

import messages from './messages'

const Text = styled.div``

const Rule = styled.div<{ prefix?: string }>`
  /* If it has a prefix (and/or) use that, otherwise use a comma since it is the beginning of a new rule */
  &:before {
    content: ' ${props => props.prefix} ';
  }
`

const RuleText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;

  ${Text}, ${Rule} {
    display: inline;
  }

  ${Text} {
    /* Insert a comma between audiences */
    &:before {
      content: ', ';
    }

    /* Remove the command if it's the first audience */
    &:first-child {
      &:before {
        content: '';
      }
    }
  }
`

/**
 * Mapping for which language prefix to use for certain attributes
 * In order to produce a more natural sounding sentence
 */
const prefixAttributeMapping = (filter: Filter) => {
  const attribute: Filter['attribute'] = get(filter, 'attribute')
  const isEqual = filter.operator === 'eq'
  let message: MessageDescriptor

  if (attribute) {
    if (attribute.type === 'boolean') {
      // Forget about the value, as "boolean" types only can have "true" as the value (?)
      message = isEqual ? messages.attributeBooleanTruthy : messages.attributeBooleanFalsyPrefix
      return (
        <FormattedMessage
          {...message}
          values={{ attributeName: <span className="font-bold">{attribute.displayName}</span> }}
        />
      )
    }

    const valueDisplay = filter.displayValue || filter.value
    switch (attribute.scimAttributeName) {
      case 'favoriteColor':
      case 'title':
      case 'id':
      case 'employeeNumber':
      case 'displayName': {
        message = isEqual ? messages.attributePossesive : messages.notAttributePossesive
        return (
          <FormattedMessage
            {...message}
            values={{ attributeName: attribute.displayName, value: <span className="font-bold">{valueDisplay}</span> }}
          />
        )
      }
      case 'userName':
      case 'individual': {
        message = isEqual ? messages.attributePersonal : messages.notAttributePersonal
        return <FormattedMessage {...message} values={{ value: <span className="font-bold">{valueDisplay}</span> }} />
      }
      default: {
        message = isEqual ? messages.attributeInclusive : messages.notAttributeInclusive
        return (
          <FormattedMessage
            {...message}
            values={{ attributeName: attribute.displayName, value: <span className="font-bold">{valueDisplay}</span> }}
          />
        )
      }
    }
  }
}

// Join each audience filter together into a human-readable JSX element array of words
const defineRule = (filters: Filter[]) => {
  const prefixMessage = messages.ruleStartPrefixUser

  return filters.reduce((prev, filter: Filter, idx) => {
    if (filter.value !== undefined && filter.value !== '') {
      if (idx === 0) {
        return [
          ...prev,
          <Rule key={`${filter.id}-${idx}`}>
            <FormattedMessage {...prefixMessage} /> {prefixAttributeMapping(filter)}
          </Rule>
        ]
      } else {
        return [
          ...prev,
          <Rule key={`${filter.id}-${idx}`} prefix={filter.conditional || 'and'}>
            {prefixAttributeMapping(filter)}
          </Rule>
        ]
      }
    }
    return prev
  }, [] as JSX.Element[])
}

interface Props {
  audiences: Audience[]
}
/**
 * AudienceSimpleRule - Translates an audience array into a human readable sentence
 *
 * Justification over existing logic: The existing logic only handles generating a sentence per "Filter"
 * This is being displayed at the level adjacent to "TaskGroup", so must also encapsulate the entire "Audience" array
 */
const AudienceSimpleRule = ({ audiences }: Props) => {
  const [audienceText, setAudienceText] = useState<{ id: string; rules: JSX.Element[] }[] | undefined>([])

  const allAudienceRule = {
    id: '123',
    rules: [
      <Rule key="123">
        <FormattedMessage {...messages.ruleAllUsers} />
      </Rule>
    ]
  }

  useEffect(() => {
    if (audiences && audiences.length > 0) {
      const filterText = audiences.reduce(
        (prev, audience: Audience) => {
          const rules = defineRule(audience.filters)
          return [...prev, { id: audience.id, rules: rules }]
        },
        [] as { id: string; rules: JSX.Element[] }[]
      )

      // If ANY of the audiences is empty, show "All Users" because it's redundant to display the other rules
      if (filterText.some(filter => filter.rules.length === 0)) {
        setAudienceText(undefined)
      } else {
        setAudienceText(filterText)
      }
    } else {
      setAudienceText(undefined)
    }
  }, [audiences])

  const ruleText = audienceText || [allAudienceRule]

  return (
    <RuleText className="mt-2 text-sm">
      {ruleText.length > 0 && (
        <FormattedMessage
          {...{ ...messages.audienceSentence }}
          values={{
            audienceText: (
              <>
                {ruleText.map((audience, index: number) => {
                  return index === 0 ? <span>{audience.rules}</span> : <Text key={audience.id}>{audience.rules}</Text>
                })}
                {'.'}
              </>
            )
          }}
        />
      )}
    </RuleText>
  )
}

export default AudienceSimpleRule
